import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "@/lib/motion";
import { toast } from "sonner";
import {
    Leaf, Award, ChefHat, Gift, Truck, ShieldCheck, ArrowRight, Instagram, Mail,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const B = "/img/";
const REAL_SHOTS = [
    B + "original.jpg",
    B + "cheesecake.jpg",
    B + "lemon.jpg",
    B + "matcha.jpg",
    B + "peach.jpg",
    B + "strawberry.jpg",
    B + "strawberry-matcha.jpg",
    B + "caramel.jpg",
];

/* ---------------- Why Sweet Pearls ---------------- */
const VALUES = [
    { icon: Leaf, title: "Made Fresh", desc: "Whipped up every morning — never frozen, never mass-produced." },
    { icon: Award, title: "Premium Ingredients", desc: "Real vanilla bean, ripe bananas and cream you can taste." },
    { icon: ChefHat, title: "Small Batches", desc: "Handcrafted by pastry hands, folded slow and layered with care." },
    { icon: Gift, title: "Parties & Gifts", desc: "Trays, boxes and gift packaging for every celebration." },
    { icon: Truck, title: "Pickup & Delivery", desc: "Grab it local or have it delivered chilled to your door." },
    { icon: ShieldCheck, title: "Secure Ordering", desc: "Encrypted checkout with Apple Pay, Google Pay & cards." },
];

export function WhySweetPearls() {
    return (
        <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
            <Reveal>
                <p className="text-sm font-semibold uppercase tracking-widest text-caramel">Why Sweet Pearls</p>
                <h2 className="mt-2 max-w-2xl font-serif text-3xl font-semibold leading-tight text-roast sm:text-4xl lg:text-5xl">
                    A dessert worth <span className="italic text-caramel">savoring</span> — and worth sharing.
                </h2>
            </Reveal>
            <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {VALUES.map((v, i) => (
                    <Reveal key={v.title} delay={i * 0.06}>
                        <div className="group h-full rounded-3xl border border-caramel/10 bg-white p-7 shadow-warm transition-all hover:-translate-y-1.5 hover:shadow-warm-lg">
                            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-banana/25 text-caramel transition-colors group-hover:bg-banana">
                                <v.icon size={26} strokeWidth={1.6} />
                            </div>
                            <h3 className="mt-5 font-serif text-xl text-roast">{v.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-mocha">{v.desc}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

/* ---------------- Build a Box teaser ---------------- */
export function BuildBoxTeaser() {
    return (
        <section className="relative overflow-hidden bg-caramel-dark py-24 text-cream-50">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-banana/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:px-8">
                <Reveal>
                    <p className="text-sm font-semibold uppercase tracking-widest text-banana">Build a Pearl Box</p>
                    <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
                        Mix, match & build your own <span className="italic text-banana">variety box</span>.
                    </h2>
                    <p className="mt-5 max-w-md text-cream-100/80">
                        Pick a box size, tap to add your favorite flavors, and watch it fill up in real time. The perfect gift or party centerpiece.
                    </p>
                    <Link to="/build-a-box" data-testid="teaser-build-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-banana px-7 py-3.5 font-semibold text-roast shadow-pill transition-all hover:bg-banana-hover active:scale-95">
                        Start Building <ArrowRight size={18} />
                    </Link>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="grid grid-cols-3 gap-3">
                        {[0, 1, 2, 3, 4, 5].map((k) => (
                            <motion.div
                                key={k}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: k * 0.08 }}
                                className="aspect-square rounded-2xl border border-cream-50/15 bg-cream-50/10 backdrop-blur-sm"
                                style={{ backgroundImage: `url(${REAL_SHOTS[k]})`, backgroundSize: "cover", backgroundPosition: "center" }}
                            />
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

/* ---------------- Product Story ---------------- */
export function ProductStory() {
    return (
        <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
            <div className="grid items-center gap-14 lg:grid-cols-2">
                <Reveal>
                    <div className="relative">
                        <div className="overflow-hidden rounded-[3rem] rounded-tr-lg border border-caramel/10 shadow-warm-lg">
                            <img src="/img/ingredients.jpg" alt="Fresh bananas and baking ingredients on a wooden board" className="h-[520px] w-full object-cover" />
                        </div>
                        <div className="absolute -bottom-6 -left-6 hidden rounded-3xl bg-banana p-6 shadow-pill sm:block">
                            <p className="font-serif text-4xl text-roast">Est.</p>
                            <p className="font-serif text-2xl text-roast">Small batch</p>
                        </div>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="text-sm font-semibold uppercase tracking-widest text-caramel">Our Story</p>
                    <h2 className="mt-2 font-serif text-3xl font-semibold leading-tight text-roast sm:text-4xl lg:text-5xl">
                        Why we call it <span className="italic text-caramel">Pearls</span>.
                    </h2>
                    <p className="mt-6 text-lg leading-relaxed text-roast/80">
                        <span className="float-left mr-3 font-serif text-6xl leading-[0.8] text-caramel">E</span>
                        very cup is layered like a treasure — velvety custard, tender banana, and golden wafers stacked into little pearls of comfort. What began in a home kitchen, folding one bowl at a time, grew into a small-batch obsession with getting each layer just right.
                    </p>
                    <p className="mt-4 leading-relaxed text-mocha">
                        We named it Sweet Pearls because a great dessert is rare, precious, and made to be shared. No shortcuts, no fillers — just the good stuff, whipped fresh every day.
                    </p>
                    <Link to="/about" className="mt-8 inline-flex items-center gap-2 font-semibold text-caramel hover:underline">
                        Read the full story <ArrowRight size={18} />
                    </Link>
                </Reveal>
            </div>
        </section>
    );
}

/* ---------------- Catering teaser ---------------- */
export function CateringTeaser() {
    return (
        <section className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
                <div className="relative overflow-hidden rounded-[2.5rem] bg-roast px-8 py-14 text-cream-50 sm:px-14">
                    <div className="absolute right-0 top-0 h-full w-1/2 opacity-30" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1520512202623-51c5c53957df?crop=entropy&cs=srgb&fm=jpg&q=80&w=900)", backgroundSize: "cover", backgroundPosition: "center", maskImage: "linear-gradient(to left, black, transparent)" }} />
                    <div className="relative max-w-xl">
                        <p className="text-sm font-semibold uppercase tracking-widest text-banana">Catering & Events</p>
                        <h2 className="mt-3 font-serif text-3xl font-semibold sm:text-4xl">Sweeten your next celebration</h2>
                        <p className="mt-4 text-cream-100/80">Birthdays, weddings, baby showers, corporate events and holidays — our pudding trays and party boxes make any gathering unforgettable.</p>
                        <Link to="/catering" data-testid="teaser-catering-btn" className="mt-7 inline-flex items-center gap-2 rounded-full bg-banana px-7 py-3.5 font-semibold text-roast shadow-pill transition-all hover:bg-banana-hover active:scale-95">
                            Request Catering Quote <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </Reveal>
        </section>
    );
}

/* ---------------- Social proof ---------------- */
const GALLERY = REAL_SHOTS;
export function SocialProof() {
    return (
        <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
            <Reveal className="text-center">
                <p className="text-sm font-semibold uppercase tracking-widest text-caramel">#SweetPearls</p>
                <h2 className="mx-auto mt-2 max-w-2xl font-serif text-3xl font-semibold text-roast sm:text-4xl">Tag us for a chance to be featured</h2>
                <p className="mx-auto mt-3 max-w-lg text-mocha">Real jars, real celebrations, real customers. Share your Sweet Pearls moment on Instagram.</p>
            </Reveal>
            <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                {GALLERY.map((g, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                        <a href="#" className="group relative block aspect-square overflow-hidden rounded-2xl">
                            <img src={g} alt="Customer" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                            <span className="absolute inset-0 grid place-items-center bg-roast/0 text-cream-50 opacity-0 transition-all group-hover:bg-roast/40 group-hover:opacity-100">
                                <Instagram size={28} />
                            </span>
                        </a>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

/* ---------------- Newsletter ---------------- */
export function Newsletter() {
    const [email, setEmail] = useState("");
    const [sms, setSms] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter", { email, sms: sms || null });
            toast.success("You're on the list! 🍌", { description: `Use code ${data.code} for 10% off your first order.` });
            setEmail(""); setSms("");
        } catch {
            toast.error("Please enter a valid email.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="mx-auto max-w-7xl px-5 pb-8 lg:px-8">
            <div className="relative overflow-hidden rounded-[2.5rem] bg-banana px-8 py-14 sm:px-14">
                <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-cream-50/40 blur-2xl" />
                <div className="relative grid items-center gap-8 lg:grid-cols-2">
                    <div>
                        <div className="inline-flex items-center gap-2 rounded-full bg-roast/10 px-3 py-1 text-sm font-semibold text-roast"><Mail size={15} /> 10% off your first order</div>
                        <h2 className="mt-4 font-serif text-3xl font-semibold text-roast sm:text-4xl">Get Something Sweet in Your Inbox</h2>
                        <p className="mt-3 max-w-md text-roast/70">Join the Sweet Pearls list for new flavors, special offers, local pop-ups, and exclusive drops.</p>
                    </div>
                    <form onSubmit={submit} className="space-y-3" data-testid="newsletter-form">
                        <Input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="h-12 rounded-full border-roast/20 bg-cream-50 px-5 text-roast placeholder:text-mocha focus-visible:ring-caramel" />
                        <Input data-testid="newsletter-sms" type="tel" value={sms} onChange={(e) => setSms(e.target.value)} placeholder="Mobile for SMS (optional)" className="h-12 rounded-full border-roast/20 bg-cream-50 px-5 text-roast placeholder:text-mocha focus-visible:ring-caramel" />
                        <Button type="submit" disabled={loading} data-testid="newsletter-submit" className="h-12 w-full rounded-full bg-roast text-base text-cream-50 hover:bg-caramel-dark">
                            {loading ? "Joining…" : "Join & Save 10%"}
                        </Button>
                    </form>
                </div>
            </div>
        </section>
    );
}
